// src/socket/SocketProvider.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/axios";

const WS_NAMESPACE = "/ws";
const WS_PATH = "/ws/socket.io";

type SocketCtx = { socket: Socket | null; connected: boolean };
const SocketContext = createContext<SocketCtx>({ socket: null, connected: false });
export const useSocket = () => useContext(SocketContext);

function getApiOriginFromAxios(): string {
  const base = (api.defaults.baseURL || "").replace(/\/+$/, "");
  if (!base) return window.location.origin.replace(":3000", ":3001");
  return base.endsWith("/api") ? base.slice(0, -4) : base;
}

const stripBearer = (t?: string | null) =>
  (t || "").replace(/^Bearer\s+/i, "").trim();

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth() as any;
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const jwt = stripBearer(token);
    if (!jwt) {
      setSocket(null);
      setConnected(false);
      return;
    }

    const API_ORIGIN = getApiOriginFromAxios();
    const url = `${API_ORIGIN.replace(/\/$/, "")}${WS_NAMESPACE}`;

    const s = io(url, {
      path: WS_PATH,
      auth: { token: jwt },              // ✅ raw JWT (no "Bearer ")
      transports: ["websocket", "polling"],
      withCredentials: true,
      forceNew: true,                    // ✅ important: avoids stale auth reuse
      reconnection: true,
    });

    setSocket(s);

    s.on("connect", () => setConnected(true));
    s.on("disconnect", () => setConnected(false));

    // ✅ add this so you can actually see auth failures
    s.on("connect_error", (err) => {
      console.error("[socket] connect_error:", err?.message || err);
      setConnected(false);
    });

    return () => {
      s.removeAllListeners();
      s.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, [token]);

  const value = useMemo(() => ({ socket, connected }), [socket, connected]);
  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
